<script setup lang="ts">
  import { cn } from '../../../lib/utils'
  import { useFormFieldProvider } from '../gat-form-field.vue'
  import { type HTMLAttributes } from 'vue'
  import GatIcon from '../../gat-icon/gat-icon.vue'
  import { TooltipHint } from '../../tooltips'
  import { Label } from '../../ui/label'

  const {
    inputLabel,
    name,
    hasErrorMessages,
    labelIcon,
    tooltipHint,
    required,
    showOptional,
    value,
    isValid,
    isDisabled,
    isReadonly,
    isLoading,
    errorMessages,
    prependInnerIcon,
    appendInnerIcon,
    inputClass,
    isClearable,
    detailsIsVisible,
    hint,
    dataTestid,
    validationErrorMessages
  } = useFormFieldProvider()

  const props = defineProps<{
    class?: HTMLAttributes['class']
  }>()

  const emit = defineEmits<{
    (e: 'clear'): void
  }>()
</script>

<template>
  <div :class="cn('flex flex-col items-start gap-1.5', props.class)">
    <div
      class="flex w-full items-center gap-0"
      v-if="inputLabel || $slots['label-content']"
    >
      <Label
        v-if="inputLabel"
        :for="name"
        :class="['items-center gap-1', { 'text-error': hasErrorMessages }]"
      >
        <gat-icon
          v-if="labelIcon"
          :icon="labelIcon"
          :class="['text-soft size-3.5']"
        />
        {{ inputLabel }}
        <TooltipHint v-if="tooltipHint" :text="tooltipHint" />
        <span
          v-if="!required && showOptional !== false"
          :class="[
            {
              'text-soft dark:text-slate-400': !hasErrorMessages
            },
            'font-normal'
          ]"
          >(Optional)</span
        >
      </Label>
      <slot name="label-content" />
    </div>

    <div
      :class="[
        'group/input relative flex w-full items-center',
        { 'cursor-not-allowed': isDisabled }
      ]"
    >
      <div
        v-if="isReadonly"
        class="text-soft w-full bg-transparent px-1 py-1 text-sm transition-colors"
        :class="[{ 'h-9': !isReadonly }]"
      >
        <slot name="readonly-content">
          {{ value ?? 'No data' }}
        </slot>
      </div>
      <slot
        v-bind="{
          isValid,
          errorMessages,
          name,
          disabled: isDisabled,
          as: 'default',
          inputClass: cn(
            'border-input bg-background shadow-none placeholder:transition placeholder:duration-200 placeholder:ease-out group-hover/input:placeholder:text-soft dark:bg-white/5 dark:placeholder:text-neutral-500 focus-visible:ring-primary flex h-9 w-full rounded-md border px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50',
            {
              'pl-6.5': prependInnerIcon,
              'pr-6.5': appendInnerIcon || isLoading,
              'border-error focus-visible:ring-error': hasErrorMessages
            },
            inputClass
          )
        }"
      />
      <span
        v-if="prependInnerIcon || $slots['prepend-inner']"
        class="absolute inset-y-0 start-0 flex items-center justify-center px-2"
      >
        <slot name="prepend-inner" />
        <gat-icon
          v-if="prependInnerIcon"
          v-bind="prependInnerIcon"
          :class="cn(['text-soft size-3.5'], prependInnerIcon.class)"
        />
      </span>
      <span
        v-if="appendInnerIcon || isLoading || $slots['append-inner']"
        class="absolute inset-y-0 end-0 flex items-center justify-center px-2"
      >
        <slot name="append-inner" />
        <gat-icon
          v-if="appendInnerIcon"
          v-bind="appendInnerIcon"
          :class="cn(['text-soft size-3.5'], appendInnerIcon.class)"
        />
        <gat-icon
          v-if="isClearable"
          icon="X"
          :disabled="isDisabled"
          :class="['text-soft size-3.5 cursor-pointer']"
          @click="emit('clear')"
        />
        <gat-icon
          v-if="isLoading"
          icon="LoaderCircle"
          :disabled="isDisabled"
          :class="['text-soft size-3.5 animate-spin']"
        />
      </span>
    </div>
    <div
      v-if="detailsIsVisible"
      class="mt-1 overflow-hidden text-xs"
      :style="{
        transition: 'all .3s',
        maxHeight:
          detailsIsVisible && (hint || hasErrorMessages) ? '200px' : '0px'
      }"
    >
      <span
        v-if="hasErrorMessages"
        class="text-error text-xs"
        :data-testid="`${dataTestid}-validation`"
      >
        {{ Array.from(validationErrorMessages).join(', ') }}
      </span>
      <div v-else :class="['text-soft', { 'opacity-0': hint === undefined }]">
        {{ hint }}
      </div>
    </div>
  </div>
</template>
